import { useNavigation } from 'expo-router';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';

import { PointsProgress } from '@fhs/loyalty/src/components';
import { isWeb } from '@fhs/utils';

import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';
import { useRewardTiers } from '../../queries/loyalty.queries';

interface Props {
  style?: StyleProp<ViewStyle>;
  loading?: boolean;
}

export const RewardsPointsProgress = forwardRef<View, Props>(function RewardsPointsProgress(
  { style, loading },
  ref
) {
  const { data: rewardTiers, isLoading: isLoadingRewardTiers } = useRewardTiers();
  const { loyaltyUser, loyaltyUserReady } = useLoyaltyLegacyStates();
  const [enableAnimation, setEnableAnimation] = useState(isWeb);
  const navigation = useNavigation();
  const isLoading = loading || !loyaltyUserReady || isLoadingRewardTiers;

  useEffect(() => {
    const unsubscribe = navigation.addListener('transitionEnd' as any, () => {
      setEnableAnimation(true);
    });
    return unsubscribe;
  }, [navigation]);

  const tiers = useMemo(() => {
    if (!rewardTiers?.length) {
      return [];
    }

    // Deduplicate by point value so the progress bar only shows unique thresholds
    const seen = new Set<number>();
    const pointsProgressTiers = rewardTiers.reduce<{ id: string; value: number }[]>(
      (acc, rewardTier) => {
        if (!seen.has(rewardTier.points)) {
          seen.add(rewardTier.points);
          acc.push({ id: rewardTier.id, value: rewardTier.points });
        }
        return acc;
      },
      []
    );

    return [{ id: '0', value: 0 }, ...pointsProgressTiers];
  }, [rewardTiers]);

  return (
    <PointsProgress
      ref={ref}
      points={loyaltyUser?.points}
      loading={isLoading}
      enableAnimation={enableAnimation}
      tiers={tiers}
      style={style}
    />
  );
});
